import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { LocalStorageKeys } from "../../../core/storage/local-storage";
import { CompanyService } from "../../../modules/portal/companies/company.service";
import { Company } from "../../../modules/portal/companies/company.type";
import { PartnerService } from "../../../modules/portal/partners/partner.service";
import { Partner } from "../../../modules/portal/partners/partner.type";
import { AnyObject } from "../../types/app.type";
import { UserInfo } from "../../types/user-info";
import { setFilterCondition } from "../../utils/helpers/common.helper";
import { QuestionBase } from "../dynamic-form/question-base";
import { DropdownOption, DropdownQuestion } from "../dynamic-form/question-dropdown";
import { TextboxQuestion } from "../dynamic-form/question-textbox";
import { ViewAs } from "./view-as";

@Injectable()
export class ViewAsService {
  private viewAs: ViewAs;

  constructor(private companyService: CompanyService, private partnerService: PartnerService) {}

  /** Save the selected partner or company in the local storage, and set the ViewAs instance */
  async saveInLocalStorage({ companyId, partnerId }: Partial<Pick<ViewAs, "companyId" | "partnerId">>): Promise<void> {
    let company = null;
    let partner = null;

    if (companyId) {
      company = await this.companyService.getCompany(companyId, { relations: { partner: true } }).toPromise();
      localStorage.setItem(LocalStorageKeys.viewAs, JSON.stringify({ company }));
    } else if (partnerId) {
      partner = await this.partnerService.getPartner(partnerId).toPromise();
      localStorage.setItem(LocalStorageKeys.viewAs, JSON.stringify({ partner }));
    }

    this.viewAs = new ViewAs({ partner, company });
  }

  /** Clean the "viewAs" object from the localStorage */
  clearLocalStorage(): void {
    localStorage.removeItem(LocalStorageKeys.viewAs);
  }

  /** Get the ViewAs instance */
  getViewAs = (): ViewAs => {
    // Get the instance of the ViewAs class
    if (this.viewAs) {
      return this.viewAs;
    }

    // Get the "viewAs" object from the local storage and save it as a new instance of the ViewAs class
    const localStorageViewAs = localStorage.getItem(LocalStorageKeys.viewAs);
    const { company, partner } = (JSON.parse(localStorageViewAs) as Pick<ViewAs, "company" | "partner">) || {};

    if (company || partner) {
      this.viewAs = new ViewAs({ company: company || null, partner: partner || null });
      return this.viewAs;
    }

    return null;
  };

  generateQuestions(
    options: {
      user?: UserInfo;
      companyId?: string;
      partnerId?: string;
      skipCompany?: boolean;
      skipPartner?: boolean;
      hidePartner?: boolean;
    } = {},
  ): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      !options?.skipPartner &&
        new DropdownQuestion({
          value: options?.partnerId,
          key: "partnerId",
          label: "Partner",
          asyncOptions: !options?.hidePartner && this.getPartners(),
          search: {
            filter: {
              callback: ({ value: name }) => this.getPartners({ name }),
            },
          },
          required: true,
          order: 1,
          disabled: false,
          hide: options?.hidePartner,
        }),
      !options?.skipCompany &&
        new DropdownQuestion({
          value: options?.companyId,
          key: "companyId",
          label: "Company",
          asyncOptions:
            options?.partnerId &&
            this.getCompanies({
              where: {
                // ...(options?.user?.companyId && { id: { $not: options?.user.companyId } }),
                partnerId: options?.partnerId,
              },
            }),
          dependsOn: {
            key: "partnerId",
            callback: ({ parentValue: partnerId }) =>
              !options?.partnerId &&
              this.getCompanies({
                where: {
                  // ...(options?.user?.companyId && { id: { $not: options?.user.companyId } }),
                  partnerId: partnerId,
                },
              }),
          },
          search: {
            filter: {
              parentKey: "partnerId",
              callback: ({ parentValue: partnerId, value: name }) =>
                this.getCompanies({
                  where: {
                    // ...(options?.user?.companyId && { id: { $not: options?.user.companyId } }),
                    partnerId: partnerId || options?.user.partnerId,
                    ...setFilterCondition({ name: name }, "$cont"),
                  },
                }),
            },
          },
          required: false,
          disabled: false,
          order: 2,
        }),
      /** @workaround - due an issue with the dynamic form */
      new TextboxQuestion({
        value: options?.partnerId,
        key: "-",
        label: "-",
        order: 3,
        hide: true,
      }),
    ];
    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  private getPartners(query?: { name?: string; id?: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ value: c.id, label: c.name }))));
  }

  private getCompanies(filter: { [x: string]: unknown; where?: AnyObject }): Observable<DropdownOption[]> {
    return this.companyService
      .getCompanies(filter)
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ value: c.id, label: c.name }))));
  }
}
